import React from 'react';
import PropTypes from 'prop-types';
import { format } from 'date-fns';
import { TableSkeleton } from '../../../components/loading/skeletons';
import './AppointmentsTable.css';

const sortIndicator = (active, direction) => {
  if (!active) return '↕';
  return direction === 'asc' ? '↑' : '↓';
};

const statusBadgeClass = (status) => {
  switch (status?.toLowerCase()) {
    case 'confirmed':
    case 'completed':
      return 'app-badge app-badge-success';
    case 'pending':
      return 'app-badge app-badge-warning';
    case 'cancelled':
    case 'no show':
      return 'app-badge app-badge-error';
    case 'rescheduled':
      return 'app-badge app-badge-info';
    default:
      return 'app-badge app-badge-default';
  }
};

const AppointmentsTable = ({
  loading,
  error,
  filteredAppointments,
  paginatedAppointments,
  orderBy,
  order,
  page,
  rowsPerPage,
  onRequestSort,
  onChangePage,
  onChangeOrder,
  onAppointmentClick,
}) => {
  const totalPages = Math.ceil(filteredAppointments.length / rowsPerPage);
  const start = filteredAppointments.length === 0 ? 0 : (page - 1) * rowsPerPage + 1;
  const end = Math.min(page * rowsPerPage, filteredAppointments.length);

  return (
    <div className="hour-slot-table">
      <div className="hour-slot-table__toolbar">
        <h2 className="hour-slot-table__title">
          {filteredAppointments.length} {filteredAppointments.length === 1 ? 'Appointment' : 'Appointments'} Found
        </h2>
        <div className="hour-slot-table__sort">
          <label htmlFor="hour-slot-sort">Sort by</label>
          <select
            id="hour-slot-sort"
            value={orderBy}
            onChange={(e) => onRequestSort(e.target.value)}
          >
            <option value="start">Date & Time</option>
            <option value="title">Client Name</option>
            <option value="calendar">Calendar</option>
            <option value="status">Status</option>
          </select>
          <button
            type="button"
            className="app-icon-btn"
            onClick={() => onChangeOrder(order === 'asc' ? 'desc' : 'asc')}
            title={order === 'asc' ? 'Ascending' : 'Descending'}
            aria-label={order === 'asc' ? 'Sort ascending' : 'Sort descending'}
          >
            {order === 'asc' ? '↑' : '↓'}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="app-table-wrap">
          <TableSkeleton
            rows={8}
            columns={[180, 160, 140, 120, 140]}
            label="Loading appointments"
          />
        </div>
      ) : error ? (
        <p className="hour-slot-table__alert hour-slot-table__alert--error">{error}</p>
      ) : filteredAppointments.length === 0 ? (
        <p className="hour-slot-table__alert">
          No appointments found matching your criteria. Try adjusting your filters.
        </p>
      ) : (
        <div className="app-table-wrap">
          <div className="app-table-scroll">
          <table className="app-data-table hour-slot-table__table" aria-label="Appointments table">
            <thead>
              <tr>
                <th>
                  <button type="button" className="app-table-sort" onClick={() => onRequestSort('title')}>
                    Client <span aria-hidden="true">{sortIndicator(orderBy === 'title', order)}</span>
                  </button>
                </th>
                <th>
                  <button type="button" className="app-table-sort" onClick={() => onRequestSort('start')}>
                    Date & Time <span aria-hidden="true">{sortIndicator(orderBy === 'start', order)}</span>
                  </button>
                </th>
                <th>
                  <button type="button" className="app-table-sort" onClick={() => onRequestSort('calendar')}>
                    Calendar <span aria-hidden="true">{sortIndicator(orderBy === 'calendar', order)}</span>
                  </button>
                </th>
                <th>
                  <button type="button" className="app-table-sort" onClick={() => onRequestSort('status')}>
                    Status <span aria-hidden="true">{sortIndicator(orderBy === 'status', order)}</span>
                  </button>
                </th>
                <th>Duration</th>
              </tr>
            </thead>
            <tbody>
              {paginatedAppointments && paginatedAppointments.length > 0 ? (
                paginatedAppointments.map((appointment) => (
                  <tr
                    key={appointment.id}
                    className="is-clickable"
                    onClick={() => onAppointmentClick(appointment)}
                  >
                    <td>
                      <div className="hour-slot-table__client">
                        <span
                          className="hour-slot-table__swatch"
                          style={{ background: appointment.color || 'var(--color-primary, #1976d2)' }}
                          aria-hidden="true"
                        />
                        <span className="hour-slot-table__clientName">
                          {appointment.title || 'Untitled'}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="hour-slot-table__datetime">
                        {appointment.start ? format(appointment.start, 'MMM d, yyyy') : 'Invalid date'}
                      </div>
                      <div className="hour-slot-table__muted">
                        {appointment.start && appointment.end
                          ? `${format(appointment.start, 'h:mm a')} - ${format(appointment.end, 'h:mm a')}`
                          : 'Invalid time'}
                      </div>
                    </td>
                    <td>
                      <span className="app-badge app-badge-default">
                        {appointment.resource?.calendarName || '-'}
                      </span>
                    </td>
                    <td>
                      <span className={statusBadgeClass(appointment.resource?.status)}>
                        {appointment.resource?.status || 'scheduled'}
                      </span>
                    </td>
                    <td>
                      {appointment.start && appointment.end
                        ? `${Math.round((appointment.end - appointment.start) / (1000 * 60))} min`
                        : '-'}
                    </td>
                  </tr>
                ))
              ) : (
                <tr className="app-data-table-empty-row">
                  <td colSpan={5}>No appointments to display</td>
                </tr>
              )}
            </tbody>
          </table>
          </div>
          {totalPages > 1 ? (
            <div className="app-table-pager">
              <span>
                {start}–{end} of {filteredAppointments.length}
              </span>
              <button type="button" disabled={page <= 1} onClick={() => onChangePage(null, page - 1)}>
                Prev
              </button>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => onChangePage(null, page + 1)}
              >
                Next
              </button>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

AppointmentsTable.propTypes = {
  loading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  filteredAppointments: PropTypes.array.isRequired,
  paginatedAppointments: PropTypes.array.isRequired,
  orderBy: PropTypes.string.isRequired,
  order: PropTypes.string.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onChangePage: PropTypes.func.isRequired,
  onChangeOrder: PropTypes.func.isRequired,
  onAppointmentClick: PropTypes.func.isRequired,
  getStatusColor: PropTypes.func,
};

export default AppointmentsTable;
